import { grpcClientFactory } from '../../grpcClientFactory';
import { GrpcCallError } from '../../../utils/grpcErrors';
class CollaborationService {
    createAuthenticatedClient() {
        return grpcClientFactory.createAuthenticatedUserProjectsClient({ enableCompression: true });
    }
    async invite(projectId, email, firstName, lastName, role, acknowledgeSeatCharge) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Request = proto.landscope.mapviewer.v1.InviteCollaboratorRequest;
                const request = new Request();
                request.setProjectId(projectId);
                request.setEmail(email);
                if (firstName) {
                    request.setFirstName(firstName);
                }
                if (lastName) {
                    request.setLastName(lastName);
                }
                if (role) {
                    request.setRole(role);
                }
                if (acknowledgeSeatCharge) {
                    request.setAcknowledgeSeatCharge(true);
                }
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.inviteCollaborator(request, metadata, (err, response) => {
                    if (err) {
                        console.error('Failed to invite collaborator:', err);
                        reject(new Error(err.message || 'Failed to invite collaborator'));
                        return;
                    }
                    if (!response) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: response.getSuccess(),
                        message: response.getMessage(),
                        userWasCreated: response.getUserWasCreated(),
                        userId: response.getUserId(),
                        userNotFound: response.getUserNotFound()
                    });
                });
            }
            catch (error) {
                console.error('Failed to invite collaborator:', error);
                reject(new Error(error.message || 'Failed to invite collaborator'));
            }
        });
    }
    async remove(projectId, userId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Request = proto.landscope.mapviewer.v1.RemoveCollaboratorRequest;
                const request = new Request();
                request.setProjectId(projectId);
                request.setUserId(userId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.removeCollaborator(request, metadata, (err, response) => {
                    if (err) {
                        console.error('Failed to remove collaborator:', err);
                        reject(new Error(err.message || 'Failed to remove collaborator'));
                        return;
                    }
                    if (!response) {
                        reject(new Error('No response received'));
                        return;
                    }
                    resolve({
                        success: response.getSuccess(),
                        message: response.getMessage()
                    });
                });
            }
            catch (error) {
                console.error('Failed to remove collaborator:', error);
                reject(new Error(error.message || 'Failed to remove collaborator'));
            }
        });
    }
    async list(projectId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Request = proto.landscope.mapviewer.v1.ListCollaboratorsRequest;
                const request = new Request();
                request.setProjectId(projectId);
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.listCollaborators(request, metadata, (err, response) => {
                    if (err) {
                        console.error('Failed to list collaborators:', err);
                        reject(new GrpcCallError(err.message || 'Failed to list collaborators', err.code));
                        return;
                    }
                    if (!response) {
                        reject(new Error('No response received'));
                        return;
                    }
                    const collaboratorsList = response.getCollaboratorsList() || [];
                    const collaborators = collaboratorsList.map((c) => ({
                        userId: c.getUserId(),
                        email: c.getEmail(),
                        firstName: c.getFirstName(),
                        lastName: c.getLastName(),
                        addedAt: c.getAddedAt(),
                        status: c.getStatus(),
                        collaboratorType: c.getCollaboratorType ? c.getCollaboratorType() : 'regular',
                        expiresAt: c.hasExpiresAt && c.hasExpiresAt() ? c.getExpiresAt() : undefined,
                        role: c.getRole ? c.getRole() || 'collaborator' : 'collaborator'
                    }));
                    resolve({ collaborators });
                });
            }
            catch (error) {
                console.error('Failed to list collaborators:', error);
                reject(new Error(error.message || 'Failed to list collaborators'));
            }
        });
    }
    // Files a project shared with the current user into one of their
    // workspaces (display only — the grant is untouched). Pass null to
    // un-file: the share shows in every workspace again.
    async setSharedProjectHome(projectId, homeAccountId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated');
        }
        return new Promise((resolve, reject) => {
            try {
                const Request = proto.landscope.mapviewer.v1.SetSharedProjectHomeRequest;
                const request = new Request();
                request.setProjectId(projectId);
                if (homeAccountId) {
                    request.setHomeAccountId(homeAccountId);
                }
                const client = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                client.setSharedProjectHome(request, metadata, (err) => {
                    if (err) {
                        reject(new Error(err.message || 'Failed to file shared project'));
                        return;
                    }
                    resolve();
                });
            }
            catch (e) {
                reject(new Error(`Failed to file shared project: ${e}`));
            }
        });
    }
}
export const collaborationService = new CollaborationService();
